import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Rocket, CheckCircle, Lock } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const PERKS = [
  'Trade 1,000+ real stocks with $25,000 virtual cash',
  'Play 6 game modes + earn XP, rank, and bonus cash',
  'Daily quiz — earn +$50 per perfect score',
  'Unlock Indian Stock Market at Master rank',
  'Compete on the leaderboard',
];

const UpgradePrompt = ({ open, onClose, action = 'do this' }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  if (!open) return null;

  const goRegister = () => {
    logout(); // clears guest token
    // window.location set by logout -> '/'
    setTimeout(() => navigate('/'), 50);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="upgrade-prompt"
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md glass-card overflow-hidden relative"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-300 z-10"
            data-testid="upgrade-prompt-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>

          <div className="bg-gradient-to-br from-yellow-400 via-yellow-500 to-orange-500 p-7 pb-12 relative text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-black/25 backdrop-blur mb-3">
              <Lock size={30} weight="fill" className="text-white" />
            </div>
            <p className="text-black/70 text-xs font-bold uppercase tracking-widest mb-1">Guest Mode</p>
            <h2 className="text-2xl font-unbounded font-bold text-white">
              Sign up to {action}
            </h2>
          </div>

          <div className="-mt-7 mx-5 mb-5 glass rounded-2xl p-5">
            <p className="text-zinc-300 text-sm mb-4 text-center">
              You're exploring TeenVest as a guest. Create a <span className="text-gold font-bold">free account</span> to unlock everything:
            </p>
            <ul className="space-y-2 mb-5">
              {PERKS.map((perk, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
                  <CheckCircle size={16} weight="fill" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-zinc-500 text-center">
              Free forever. Just an email + password. Under 30 seconds.
            </p>
          </div>

          <div className="px-5 pb-5 flex flex-col gap-2">
            <Button
              onClick={goRegister}
              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold py-5 rounded-full"
              data-testid="upgrade-prompt-signup"
            >
              <Rocket size={18} weight="fill" className="mr-2" />
              Create Free Account
            </Button>
            <Button
              variant="ghost"
              onClick={onClose}
              className="w-full text-zinc-400 hover:text-white"
              data-testid="upgrade-prompt-keep-browsing"
            >
              Keep Browsing as Guest
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UpgradePrompt;
